import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FaFilter, FaSearch } from "react-icons/fa";
import { HiOutlineRectangleGroup } from "react-icons/hi2";
import { PiChartLineLight } from "react-icons/pi";
import ThemeToggle from './ThemeToggle';
import { Filters } from './Filters';
import { useTheme } from '../contexts/ThemeContext';
import "../styles/globalHeader.css";

import homeChartOptions from '../Pages/Home/chartOptions';
import searchOptions from '../Pages/Home/filterSearch';
import ExpandToggle from './ExpandToggle';
import FleetViewToggle from './FleetViewToggle';
import { menusService } from '../Services/menus.service';
import { TooltipIcon } from '../Pages/Components/Tooltips/TooltipInfos';

const GlobalHeader = ({ showFilters = false, optionVehicles = [] }) => {
  const { t, i18n } = useTranslation();
  const { pageTitle } = useSelector(
    (rootReducer) => rootReducer.layoutDataReducer
  );

  const { vehicleSelected } = useSelector(
    (rootReducer) => rootReducer.vehicleSelectedReducer
  );

  const { isDark } = useTheme();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [allowedMenuIds, setAllowedMenuIds] = useState([]);
  const [filterableCharts, setFilterableCharts] = useState([]);

  // Função para retornar a chave de tooltip por página
  const getTooltipKeyByPage = () => {
    switch (pageTitle) {
      case "Radar":
        return "RADAR";
      case "Posições Detalhadas":
        return "POSICOES_DETALHADAS";
      case "Veículo/Apelido":
        return "APELIDO";
      case "Logs":
        return "LOGS";
      case "Protocolos de Atendimento":
        return "PROTOCOLS";
      case "Cadastro de Grupos":
        return "GROUPS";
      case "Veiculos vinculados":
        return "BIND_VEHICLES";
      case "Vincular usuários aos grupos":
        return "GROUP_USERS";
      case "Pontos de Visita":
        return "CONTROL_POINT";
      case "Cerca Eletrônica":
        return "ELETRONIC_FENCES";
      case "Área de Risco":
        return "RISK_AREA";
      case "Deslocamento":
        return "GRID_JOURNEY_WORK";
      case "Posicionamento":
        return "POSITION";
      case "Distância Percorrida":
        return "DISTANCE";
      case "Visitas por veículos":
        return "GRID_VISIT_AMOUNT";
      case "Veículos em visitas":
        return "GRID_PERIOD_VISITS";
      case "Controle de Velocidade":
        return "GRID_SPEED_CONTROL";
      case "Motoristas Cadastrados":
        return "DRIVERS_MANAGEMENT";
      case "Dashboard de gestão":
      case "Dashboard gerencial":
      case "Dashboard Gerencial":
        return "DASHBOARD";
      case "Minha Frota":
        return "MINHA_FROTA";
      case "Veículo mais próximo":
        return "VEICULO_MAIS_PROXIMO";
      case "Perfil":
        return "USER_PROFILE";
      case "Alterar Senha":
        return "CHANGE_PASSWORD";
      case "Visualizar Cliente":
        return "COMPANY_INFO";
      case "Usuários do Cliente":
        return "USER_PROFILE_CLIENT";
      case "Cadastro de itens":
        return "MAINTENANCE_ITEM";
      case "Agendamento de itens":
        return "MAINTENANCE_SCHEDULING";
      case "Acompanhamento de itens":
        return "MAINTENANCE_SHIPPING";
      case "Manutenções Pendentes":
        return "MAINTENANCE_PENDINGS";
      case "Manutenções Concluídas":
        return "MAINTENANCE_COMPLETED";
      case "Registro de Odometro":
        return "MAINTENANCE_REGISTER_KM";
      case "Vínculo de Comandos a Nível de Acesso":
        return "EQUIPMENT_COMMAND_ACCESS_LEVELS";
      default:
        return null; // se não tiver tooltip configurado, não mostra
    }
  };

  const tooltipKey = getTooltipKeyByPage();

  // Buscar menuIds permitidos da API
  useEffect(() => {
    const menuDashboardCharts = async () => {
      try {
        const responseCharts = await menusService.menusCharts();
        if (!responseCharts) return;

        const ids = responseCharts.map((item) => item.menuId);
        setAllowedMenuIds(ids);
      } catch (error) {
        console.log(error);
      }
    };

    menuDashboardCharts();
  }, []);


  // Filtrar gráficos e traduzir nomes
  useEffect(() => {
    const filteredCharts = homeChartOptions
      .filter((opt) => {
        if (opt.menuId === 0) return false;
        if (!opt.menuId) return true;
        return allowedMenuIds.includes(opt.menuId);
      })
      .map((opt) => ({
        ...opt,
        chart: t(opt.chart),
      }));

    setFilterableCharts(filteredCharts);
  }, [allowedMenuIds, t]);

  return (
    <div className="dashboard-header">
      {/* Título à esquerda */}
      <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: '1.5rem', paddingLeft: '10px' }}>
        <h1 className="layout-title">{pageTitle}</h1>

        {tooltipKey && (
          <TooltipIcon tooltipKey={tooltipKey} placement="bottom" />
        )}
      </div>

      {/* Área direita - filtros e ThemeToggle */}
      <div className="filters-header">
        {pageTitle === "Minha Frota" && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: "0.8rem" }}>
            <p className="dashboard-subtitle">
              Atualizado em: {new Date().toLocaleString(i18n.language)}
            </p>
            {/* Visão expandida (issue #557) — mapa grande + grid completo
                sem paginação, alternativa à visão padrão (accordion). Some
                junto com Fleet.jsx sempre que a pessoa sai da tela. Fica
                colado no ExpandToggle (mesmo visual dos dois), não no
                texto "Atualizado em". */}
            <FleetViewToggle size="xl" />
            <ExpandToggle size='xl' />
          </div>
        )}

        {pageTitle === "Posições Detalhadas" && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: "0.8rem" }}>
            <p className='dashboard-subtitle'>
              Veiculo: <strong>{vehicleSelected?.plate ?? t("HOME.VEHICLE_NOT_SELECTED")}</strong>
            </p>
          </div>
        )}

        {/* ThemeToggle */}
        <div className="theme-toggle-container">
          <ThemeToggle size="xl" />
        </div>

        {/* Ícone de Filtro (quando existirem filtros) */}
        {showFilters && (
          <div className="filter-icon-container">
            <button
              className={`filter-icon-button ${isDark ? "dark" : "light"}`}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              aria-label="Abrir/fechar filtros"
            >
              <FaFilter size={20} />
            </button>
          </div>
        )}
      </div>

      {/* Filtros (quando existirem e estiverem abertos) */}
      {showFilters && (
        <div className={`filters-dropdown ${isFilterOpen ? "open" : ""}`}>
          <div className="filters-dropdown-content">
            <div className="filters-dropdown-header">
              <h3>{t("TITLE.FILTERS")}</h3>
              <button
                className="close-filters-button"
                onClick={() => setIsFilterOpen(false)}
                aria-label="Fechar filtros"
              >
                ×
              </button>
            </div>

            <div className="dashboard-filters">
              <Filters
                id={"grafico"}
                title={t("TITLE.FILTER_CHARTS")}
                hasSubTitle={false}
                options={filterableCharts}
                icon={PiChartLineLight}
                hasIcon={true}
                isMulti={true}
                allowedMenuIds={allowedMenuIds}
                defaultSelected={[]}
              />

              <Filters
                id={"periodo"}
                title={t("TITLE.FILTER_PERIODS")}
                hasSubTitle={true}
                isMulti={false}
                subTitle={"Selecione um período"}
                options={searchOptions}
                icon={FaSearch}
                hasIcon={true}
              />

              <Filters
                id={"veiculos"}
                isMulti={false}
                title={t("TITLE.FILTER_GROUPS")}
                pageTitle={"globalHeader"}
                hasSubTitle={true}
                subTitle={"Todos os grupos"}
                options={optionVehicles}
                icon={HiOutlineRectangleGroup}
                hasIcon={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalHeader;
